import { tv } from "@ui/lib/tv";

export type FieldSize = "md" | "lg" | "xl";

export const field = tv({
  slots: {
    // pb-1 reserves space for Field.Control's 4px outset focus halo so it
    // never gets clipped by parent container overflow (modal bodies, cards,
    // popovers, etc.). Self-contained: each Field owns its own halo space.
    root: "flex min-w-0 flex-col pb-1",
    label: "font-semibold text-neutral-900",
    optional: "font-normal text-neutral-300",
    description: "pt-0.5 text-secondary",
    error: "pt-0.5 text-pink-600",
    success: "pt-0.5 text-green-700",
    control: ["flex items-center gap-1.5 overflow-clip rounded-xl bg-white p-2", "shadow-keyline"],
    leadIcon: "shrink-0 [&_svg]:size-6",
    validationIcon: "shrink-0 [&_svg]:size-5",
  },
  variants: {
    size: {
      md: {
        root: "gap-1",
        label: "text-base/6",
        description: "text-sm/5",
        error: "text-sm/5",
        success: "text-sm/5",
      },
      lg: { root: "gap-1", label: "text-lg/6", description: "text-sm/5", error: "text-sm/5", success: "text-sm/5" },
      xl: {
        root: "gap-2",
        label: "text-lg/6",
        description: "text-base/5",
        error: "text-base/5",
        success: "text-base/5",
      },
    },
    align: {
      start: { control: "items-start" },
    },
    isFocused: {
      true: { control: "shadow-[0_0_0_1px_var(--color-blue-400),0_0_0_4px_var(--color-blue-200)]" },
    },
    validationState: {
      invalid: {
        control: "shadow-[0_0_0_1px_var(--color-pink-500),0_0_0_4px_var(--color-pink-100)]",
        validationIcon: "text-pink-500",
      },
      success: {
        control: "shadow-[0_0_0_1px_var(--color-green-500),0_0_0_4px_var(--color-green-100)]",
        validationIcon: "text-green-500",
      },
    },
    isDisabled: {
      true: { root: "opacity-60" },
    },
    isLabelHidden: {
      true: { label: "sr-only" },
    },
  },
  defaultVariants: { size: "lg" },
});

export const fieldSet = tv({
  slots: {
    root: "m-0 flex min-w-0 flex-col border-0 p-0",
    legend: "font-semibold text-neutral-900",
    optional: "font-normal text-neutral-300",
    description: "text-secondary",
    list: "flex flex-col",
    item: "border-b border-b-neutral-100 last:border-b-0 last:pb-0",
  },
  variants: {
    size: {
      md: { root: "gap-2", legend: "text-base", description: "text-sm/5", list: "gap-2", item: "pb-3" },
      lg: { root: "gap-3", legend: "text-lg leading-7", description: "text-sm/5", list: "gap-3", item: "pb-6" },
      xl: { root: "gap-4", legend: "text-xl leading-7", description: "text-base/6", list: "gap-3", item: "pb-6" },
    },
    isDisabled: {
      true: { root: "opacity-60" },
    },
  },
  defaultVariants: { size: "lg" },
});
